import { createSelector } from '@opengov/compose-app/reselect';
import { selectFeatureFlag } from '@opengov/feature-flag/launchDarkly/launchDarklySelectors';

// This is useful for working with useFeatureFlag, from the FeatureFlagsProvider.
// Actual LaunchDarkly flag ids can be managed here.
export const ldFlags = {
  productReportingFeatureAccountSummaryCharts: 'product-reporting-feature-account-summary-charts',
  productReportingFeatureChartsCustomizationOptions: 'product-reporting-feature-charts-customization-options',
  productReportingFeatureChartsCustomizationTitleAndDescription:
    'product-reporting-feature-charts-customization-title-and-description',
  productFeatureReportingRollingRelativeFilter: 'product-feature-reporting-rolling-relative-filter',
  productBudgetFeatureUniqueBase: 'product-budget-feature-unique-base',
  productReportingFeatureCalculatedColumnsInAccountSummaryCharts:
    'product-reporting-feature-calculated-columns-in-account-summary-visualisation',
  productReportingFeatureCalculatedRowsInAccountSummaryCharts:
    'product-reporting-feature-calculated-rows-in-account-summary-visualisation',
  productReportingHideClassic: 'product-reporting-hide-classic',
  productReportingFeatureMultipleYAxesInAccountSummaryVisualization:
    'product-reporting-feature-multiple-y-axes-in-account-summary-visualization',
  productReportingFeatureOptionToGroupPieSlicesIntoOthers:
    'product-reporting-feature-option-to-group-pie-slices-into-others',
  multiHierarchyCoaFinancialReports: 'bnp-reporting-financial-reports',
  productReportingFeatureMdsCsvDownloadApi: 'product-reporting-feature-mds-csv-download-api',
  // BUD-18475. Read in exactly ONE place — the Details tab strip's render condition in
  // `FinancialReportContainer.tsx`. Deliberately has no reselect selector, so nothing else can
  // start depending on it and removal stays a two-line deletion. Remove once the tab has been on
  // for all entities for one release.
  productReportingFeatureMhDetailsTable: 'product-reporting-feature-mh-details-table',
  bnpHideOldEmailSettings: 'bnp-hide-old-email-settings',
  reportingPerformancePoc: 'reporting-performance-poc',
  // Runtime gate for PostHog product analytics (@opengov/analytics). Only has an effect in builds
  // that bundle the SDK (integration and local development); read by `OgAnalyticsGate`.
  productReportingFeatureEnablePosthog: 'product-reporting-feature-enable-posthog'
};

export const selectConsistentCoAEnabledFlag = createSelector(selectFeatureFlag, (getFeatureFlag) =>
  getFeatureFlag('product-budget-feature-erp-consistent-coa')
);

export const selectIncludeLabelsInAccountNumberAndEbtoDimensionsFlag = createSelector(
  selectFeatureFlag,
  (getFeatureFlag) => getFeatureFlag('rnt-include-account-label-in-canonical-coa-dimension')
);

export const selectBypassColumnMultiFilterLimitFeatureFlag = createSelector(selectFeatureFlag, (getFeatureFlag) =>
  getFeatureFlag('product-nextgen-reporting-bypass-column-multi-filter-limit')
);

export const selectUseLargeScaleQueryLimit = createSelector(selectFeatureFlag, (getFeatureFlag) =>
  getFeatureFlag('use-large-scale-query-limit')
);

export const selectStandardTemplateFeatureFlag = createSelector(
  selectFeatureFlag,
  (getFeatureFlag) => getFeatureFlag('product-nextgen-reporting-feature-templatize-standard') || false
);

export const selectPdfExportFlag = createSelector(
  selectFeatureFlag,
  (getFeatureFlag) => getFeatureFlag('product-nextgen-reporting-feature-pdf-export') || false
);

export const selectNextGenGalleryPhase1Enabled = createSelector(
  selectFeatureFlag,
  (getFeatureFlag) => getFeatureFlag('product-feature-reporting-nextgen-gallery-ph1') || false
);

export const selectNextGenGalleryPhase2Enabled = createSelector(
  selectFeatureFlag,
  (getFeatureFlag) => getFeatureFlag('product-feature-reporting-nextgen-gallery-ph2') || false
);

export const selectDatasetLinkEnabled = createSelector(
  selectFeatureFlag,
  (getFeatureFlag) => getFeatureFlag('product-nextgen-reporting-feature-report-gallery-enable-dataset-link') || false
);

export const selectDetailsTableGroupByFlag = createSelector(
  selectFeatureFlag,
  (getFeatureFlag) => getFeatureFlag('product-nextgen-reporting-feature-group-by-with-subtotals') || false
);

export const selectMultiDatasetFlag = createSelector(
  selectFeatureFlag,
  (getFeatureFlag) => getFeatureFlag('product-nextgen-reporting-feature-multidataset') || false
);

export const selectChangeDatasetFlag = createSelector(
  selectFeatureFlag,
  (getFeatureFlag) => getFeatureFlag('product-nextgen-reporting-feature-change-dataset') || false
);

export const selectOverrideRowFlag = createSelector(
  selectFeatureFlag,
  (getFeatureFlag) => getFeatureFlag('product-nextgen-reporting-feature-override-row') || false
);

export const selectExportPDFMultiDatasetFlag = createSelector(
  selectFeatureFlag,
  (getFeatureFlag) => getFeatureFlag('product-nextgen-reporting-feature-mds-pdf-export') || false
);

export const selectExpandCollapseLevelFlag = createSelector(
  selectFeatureFlag,
  (getFeatureFlag) => getFeatureFlag('product-nextgen-reporting-feature-expand-collapse-one-level') || false
);

export const selectExtraRowBreakDownOptions = createSelector(selectFeatureFlag, (getFeatureFlag) =>
  getFeatureFlag('product-nextgen-reporting-feature-extra-break-down-options', false)
);

export const selectUndoButtonMultiDatasetFlag = createSelector(
  selectFeatureFlag,
  (getFeatureFlag) => getFeatureFlag('product-nextgen-reporting-undo') || false
);

export const selectPollReportLensesFlag = createSelector(
  selectFeatureFlag,
  (getFeatureFlag) => getFeatureFlag('product-nextgen-reporting-feature-data-update-notification-status') || false
);

export const selectNextGenGridCacheFlag = createSelector(
  selectFeatureFlag,
  (getFeatureFlag) => getFeatureFlag('product-nextgen-reporting-feature-grid-cache') || false
);

export const selectReportingPerformancePocFlag = createSelector(
  selectFeatureFlag,
  (getFeatureFlag) => getFeatureFlag(ldFlags.reportingPerformancePoc) || false
);

export const selectFiscalYearFiscalPeriodCombinedColumnFlag = createSelector(selectFeatureFlag, (getFeatureFlag) => {
  return getFeatureFlag('product-nextgen-reporting-feature-fiscal-year-fiscal-period-combined-column') || false;
});

export const selectOrganizeColumnsByAccountTypeFlag = createSelector(selectFeatureFlag, (getFeatureFlag) => {
  return getFeatureFlag('product-nextgen-reporting-organize-columns-by-account-type') || false;
});

export const selectAccountSummaryMaxParallelPrefetchRequests = createSelector(selectFeatureFlag, (getFeatureFlag) => {
  return getFeatureFlag<number>('product-nextgen-reporting-max-parallel-requests', 0);
});

export const selectShowBothViewsAndLensesOnReportCreationFlag = createSelector(selectFeatureFlag, (getFeatureFlag) => {
  return getFeatureFlag('product-nextgen-reporting-show-all-data-on-create', false);
});

export const selectShowViewsInsteadOfLensesOnReportCreationFlag = createSelector(
  selectFeatureFlag,
  (getFeatureFlag) => {
    return getFeatureFlag('product-nextgen-reporting-show-views-on-create', false);
  }
);

export const selectIsCoaMigrationEnabledFlag = createSelector(selectFeatureFlag, (getFeatureFlag) => {
  return getFeatureFlag('product-budget-coa-migration-enabled', false);
});

export const selectUseRelaxedCoaCompatibleLensCheckFlag = createSelector(selectFeatureFlag, (getFeatureFlag) => {
  return getFeatureFlag('product-nextgen-reporting-use-relaxed-coa-compatible-lens-check', false);
});

export const selectDatasetLabelsFlag = createSelector(selectFeatureFlag, (getFeatureFlag) => {
  return getFeatureFlag('product-nextgen-reporting-feature-dataset-labels', false);
});

export const selectBudgetFeatureUniqueBaseFlag = createSelector(selectFeatureFlag, (getFeatureFlag) => {
  return getFeatureFlag('product-budget-feature-unique-base', false);
});

export const selectFormsAndWorkflowsFlag = createSelector(selectFeatureFlag, (getFeatureFlag) =>
  getFeatureFlag('product-budget-feature-forms-and-workflows')
);

export const selectPerformanceManagementFlag = createSelector(selectFeatureFlag, (getFeatureFlag) =>
  getFeatureFlag('product-budget-performance-management')
);

export const selectMultiHierarchyCoaFinancialReportsFlag = createSelector(selectFeatureFlag, (getFeatureFlag) =>
  getFeatureFlag(ldFlags.multiHierarchyCoaFinancialReports, false)
);

export const selectBnpHideOldEmailSettingsFlag = createSelector(selectFeatureFlag, (getFeatureFlag) =>
  getFeatureFlag(ldFlags.bnpHideOldEmailSettings, false)
);
